/**
 * LT-17 §7d — passive labour-rate staleness banner for the Estimate tab.
 *
 * Fetches `GET /tenders/{id}/labour-rate-staleness` and renders an
 * ignorable amber banner when any master labour rate used by this
 * tender has moved since the tender was created. Informational only:
 * no modal, no blocking, no auto-update. Ignoring it costs nothing.
 *
 * Why passive: rates change on the master frequently as day rates
 * drift and buffers get retuned; forcing a decision on every open
 * would be noise. The banner just tells Chris that a decision is
 * available when he wants it — and points to the Revise flow (LT-17
 * §7e) as the only place a live tender's rates actually get updated.
 */
import React, { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { api } from "../lib/api";

export default function LabourRateStalenessBanner({ tender }) {
  const [staleness, setStaleness] = useState(null);

  useEffect(() => {
    let dropped = false;
    api.get(`/tenders/${tender.id}/labour-rate-staleness`)
      .then((r) => { if (!dropped) setStaleness(r.data); })
      .catch(() => {});
    return () => { dropped = true; };
  }, [tender.id, tender.updated_at]);

  if (!staleness) return null;
  const changed = (staleness.stale_since_tender_created || []).length;
  if (changed === 0) return null;

  return (
    <div
      className="mb-3 rounded-md border px-4 py-2.5 flex items-start justify-between gap-3"
      style={{ background: "#FEF3E2", borderColor: "#F2DFB8", color: "#7A4C0A" }}
      data-testid="labour-rate-staleness-banner"
    >
      <div style={{ fontSize: 12 }}>
        <AlertTriangle size={12} className="inline -mt-0.5 mr-1.5" />
        <strong>{changed} labour rate{changed === 1 ? " has" : "s have"}</strong>{" "}
        changed since you started this tender.
        {" "}
        <span className="text-ink-3">
          The line rates on this tender still hold their original values (that's the point of the snapshot).
          If you want them repriced, use the Revise action after Issuing V1 — or edit lines individually.
        </span>
      </div>
    </div>
  );
}
